import axios from 'axios';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import * as R from 'ramda';
import { put } from 'redux-saga/effects';

import * as userActions from '@atom/actions/userActions';

import { CURRENT_SUBDOMAIN, LOGIN_ENDPOINT } from './endpoints';
import { ATOM_USER_HEADER } from './requestUtilities';

export const getToken = (): any => {
  return Cookies.get('access_token');
};

const individualAccountLogin = async (
  email: string,
  password: string,
): Promise<any> => {
  const response = await axios.post(LOGIN_ENDPOINT, { email, password });
  return response.data;
};

export async function getAccountPayload(): Promise<any> {
  return new Promise(
    (resolve: (data: any) => void, reject: (data: any) => void) => {
      try {
        const accessToken = getToken();
        const token = jwtDecode(accessToken);
        // @ts-ignore
        axios.defaults.headers[ATOM_USER_HEADER] = token.email;

        resolve(token);
      } catch (error) {
        reject(error);
      }
    },
  );
}

export const getAccountPayloadSync = (): any => {
  const accessToken = getToken();
  const token = jwtDecode(accessToken);
  return token;
};

export const updateRequestHeaders = async (): Promise<any> => {
  const token = await getAccountPayload();
  axios.defaults.headers[ATOM_USER_HEADER] = token.email;
};

export const persistAccountPayload = async (
  tokenString: string,
): Promise<any> => {
  return new Promise(
    (resolve: (data?: any) => void, reject: (data: any) => void) => {
      try {
        Cookies.set('access_token', tokenString);
        resolve();
      } catch (error) {
        reject(error);
      }
    },
  );
};

export const getUserId = async () => {
  const accountPayload = await getAccountPayload();

  if (R.isNil(accountPayload.organizations)) {
    return null;
  }
  const organization = accountPayload.organizations.find(
    (org: any): boolean => org.name === CURRENT_SUBDOMAIN,
  );

  if (!organization) {
    return null;
  }

  return organization.id;
};

export const getUserIdSync = () => {
  const accountPayload = getAccountPayloadSync();

  if (R.isNil(accountPayload.organizations)) {
    return null;
  }
  const organization = accountPayload.organizations.find(
    (org: any): boolean => org.name === CURRENT_SUBDOMAIN,
  );

  if (!organization) {
    return null;
  }

  return organization.id;
};

export const getLoggedInEmail = (): any => {
  const accessToken = getToken();
  if (!accessToken) {
    return null;
  }
  const token = jwtDecode(accessToken);

  if (R.isNil(token)) {
    return null;
  }

  // @ts-ignore
  return token.email;
};

export const tokenExists = (): boolean => {
  const accessToken = getToken();
  return !!accessToken;
};

export const isEmailVerified = (): any => {
  const accessToken = getToken();
  const token = jwtDecode(accessToken);

  // @ts-ignore
  return token.emailVerified;
};

export const logoutUser = (): Promise<any> => {
  return new Promise(
    (resolve: (data?: any) => void, reject: (data: any) => void) => {
      try {
        const allCookies = Cookies.get();

        // @ts-ignore
        Object.keys(allCookies).map(Cookies.remove);
        resolve();
      } catch (error) {
        reject(error);
      }
    },
  );
};

const isValidEmail = (email: string): boolean => {
  /* eslint-disable no-useless-escape */
  const regex =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  /* eslint-enable no-useless-escape */

  return regex.test(email);
};

const isValidPassword = (password: string): boolean => {
  if (!password) {
    return false;
  }

  // Password rules:
  // At least one uppercase letter A-Z
  // At least one lowercase letter a-z
  // At least one number 0-9
  // At least 12 characters

  const regex = new RegExp(
    /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$ %^&*-]).{12,}$/,
  );

  return regex.test(password);
};

export function* apiErrorHandler(error: any, actionCreator: any): any {
  if (error.response && error.response.status === 401) {
    yield put(userActions.triggerLogout());
  } else {
    yield put(actionCreator());
  }
}

const accountUtilities = {
  apiErrorHandler,
  getAccountPayload,
  getLoggedInEmail,
  getToken,
  getUserId,
  individualAccountLogin,
  isValidEmail,
  isValidPassword,
  isEmailVerified,
  logoutUser,
  tokenExists,
};

export default accountUtilities;
